import { useRef, useState } from "react";
import { Link, redirect, useActionData, useFetcher, useNavigation } from "react-router";
import type { ActionFunctionArgs } from "react-router";
import { useSuspenseQueries } from "@tanstack/react-query";
import type { Me, PendingInvitation, SentRequest } from "@repo/shared-types";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage } from "@/lib/session";
import { requireSessionWith } from "@/lib/guards";
import { useSession } from "@/routes/root";
import { tenantPath } from "@/lib/tenants";
import { formatDay, viewerZone } from "@/lib/times";
import { answerInvitation } from "@/lib/invitations";
import { toast } from "@/lib/toast";
import { Invitations } from "@/components/invitations";
import { WithdrawRequest } from "@/components/join-requests";
import { SpacePicture } from "@/components/avatar";
import { CreateButton } from "@/components/create-button";
import { ErrorNote, Field, SegmentedControl, useFormProblem } from "@/components/form";
import { Modal, type Outcome } from "@/components/modal";
import { ChevronRight, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyNote } from "@/components/empty-note";
import { FieldDescription } from "@/components/ui/field";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";

/**
 * The spaces page: everything that is about the person rather than
 * about one space — which they belong to, which they have been invited
 * to, which they have asked to join, and creating a new one — from a
 * sheet its button at the top opens, so the page itself is the lists.
 */

type PageData = {
  session: Me;
  invitations: PendingInvitation[];
  /** Their requests to join that nobody has answered yet. */
  sent: SentRequest[];
  /** The zone the day each was sent is read in: theirs, or the device's. */
  zone: string;
};

export async function spacesLoader({ request }: { request: Request }) {
  // An address of the old results opens the search page (0085).
  const term = new URL(request.url).searchParams.get("q")?.trim();
  if (term) throw redirect(`/spaces/search?${new URLSearchParams({ q: term })}`);
  // The memberships are already on the session, so only the invitations and
  // the requests sent need asking for, and the profile for the zone.
  await requireSessionWith(request, () =>
    Promise.all([read(queries.invitations()), read(queries.sentRequests()), read(queries.profile())]),
  );
  return null;
}

/** The page's reads, which its loader warmed. */
function usePageData(): PageData | null {
  const session = useSession();
  const [{ data: invitations }, { data: sent }, { data: profile }] = useSuspenseQueries({
    queries: [queries.invitations(), queries.sentRequests(), queries.profile()],
  });
  if (!session) return null;
  return {
    session,
    invitations: invitations.invitations,
    sent: sent.requests,
    zone: viewerZone(profile.timeZone).zone,
  };
}

type ActionResult = { error?: string; field?: string };

export async function spacesAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const intent = String(form.get("intent") ?? "");

  try {
    switch (intent) {
      case "create": {
        // Said beside its box, before anything is sent.
        const name = String(form.get("name") ?? "").trim();
        if (!name) return { error: "Give the space a name.", field: "name" };
        const created = await api.tenants.create({
          name,
          visibility: String(form.get("visibility") ?? "") || undefined,
        });
        return redirect(tenantPath(created));
      }
      case "accept":
      case "decline":
        return (await answerInvitation(intent, form)) ?? {};
      // Each says so in a toast: the line it was asked from changes, which
      // shows that something happened rather than what.
      case "join": {
        await api.tenants.requestToJoin(String(form.get("slug") ?? ""));
        toast("Asked to join. Somebody there decides next.");
        return {};
      }
      case "withdraw": {
        // One click: asking again is allowed at once, so nothing is lost.
        await api.tenants.withdrawRequest(String(form.get("slug") ?? ""));
        toast("Request withdrawn.");
        return {};
      }
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function Spaces() {
  const data = usePageData();
  // The loader let the page draw only with a session.
  return data && <SpacesPage {...data} />;
}

function SpacesPage({ session, invitations, sent, zone }: PageData) {
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";
  // The personal space is the log, which its own tab opens (decided with
  // Tiago 12 October): this page lists the spaces shared with other people.
  const spaces = session.memberships.filter(({ tenant }) => !tenant.isPersonal);
  const [creating, setCreating] = useState(false);

  return (
    <>
      {result?.error && <ErrorNote message={result.error} />}

      <Invitations invitations={invitations} busy={busy} />

      {sent.length > 0 && (
        <div className="space-y-3">
          <h2 className="section-label">Requests you've sent</h2>
          <ul className="space-y-2">
            {sent.map((request) => (
              <li key={request.id}>
                <Item variant="outline" className="bg-card">
                  {/* Its presentation page while it is public; a space gone
                      private since has none to open. */}
                  <ItemMedia>
                    <SpacePicture space={request.tenant} />
                  </ItemMedia>
                  <ItemContent className="min-w-0 wrap-anywhere">
                    <ItemTitle className="line-clamp-2">
                      {request.tenant.visibility === "public" ? (
                        <Link to={tenantPath(request.tenant)} className="hover:underline">
                          {request.tenant.name}
                        </Link>
                      ) : (
                        request.tenant.name
                      )}
                    </ItemTitle>
                    <ItemDescription>asked {formatDay(request.createdAt, zone)}, waiting for an answer</ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <WithdrawRequest slug={request.tenant.slug} busy={busy} />
                  </ItemActions>
                </Item>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="section-label">Your spaces</h2>
        {spaces.length === 0 ? (
          <EmptyNote icon={UsersRound}>You are in no spaces yet. Find one above, or start one with New space.</EmptyNote>
        ) : (
          <ul className="space-y-2">
            {spaces.map(({ tenant, role }) => (
              <li key={tenant.id}>
                {/* The whole row opens the space, though only its name is the
                    link's: what a screen reader hears is the space. */}
                <Item
                  variant="outline"
                  className="relative bg-card has-[a:hover]:bg-muted has-[a:focus-visible]:border-ring has-[a:focus-visible]:ring-3 has-[a:focus-visible]:ring-ring/30"
                >
                  <ItemMedia>
                    <SpacePicture space={tenant} />
                  </ItemMedia>
                  <ItemContent className="min-w-0 wrap-anywhere">
                    <ItemTitle className="line-clamp-2">
                      <Link to={tenantPath(tenant)} className="outline-none after:absolute after:inset-0">
                        {tenant.name}
                      </Link>
                    </ItemTitle>
                    <ItemDescription>{role}</ItemDescription>
                  </ItemContent>
                  <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                </Item>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateButton label="New space" onClick={() => setCreating(true)} />
      {creating && <NewSpace onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * A new space, in a sheet over the page: its name and who can find it, one of
 * two in sight (a phone's control, not a select), sent with the form.
 * Created, the action opens the space; refused, the sheet says why.
 */
function NewSpace({ onClose }: { onClose: () => void }) {
  const fetcher = useFetcher<Outcome>();
  const busy = fetcher.state !== "idle";
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(fetcher.data, form);
  return (
    <Modal title="New space" onClose={onClose}>
      <fetcher.Form ref={form} method="post" className="space-y-5" noValidate onChange={shown.onChange}>
        <input type="hidden" name="intent" value="create" />

        <Field label="Name" name="name" autoFocus error={shown.problem?.field === "name" ? shown.problem.error : undefined} />

        <div className="space-y-2">
          <SegmentedControl
            label="Visibility"
            name="visibility"
            options={[
              { value: "public", label: "Public" },
              { value: "private", label: "Private" },
            ]}
            value={visibility}
            onChange={setVisibility}
          />
          <FieldDescription>
            {visibility === "private" ? "Invitation only." : "Findable: people can ask to join."}
          </FieldDescription>
        </div>

        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={busy}>
            Create space
          </Button>
        </div>
      </fetcher.Form>
    </Modal>
  );
}
